import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useAutomationServersQuery } from "@/hooks/useAutomationServersQuery";
import { useWorkspacesQuery } from "@/hooks/useWorkspacesQuery";
import { LogsDialog } from "./LogsDialog";
import { useMQTTTokens } from "@/context/MQTTTokensProvider";
import MQTTService, {
  type WorkspaceCommandLogMessage,
  type WorkspaceCommandResult,
} from "@/services/MQTTService";
import type { LogEntry } from "./LogsDialog";

interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  automationServerId?: string; // Optional - if not provided, show selector
}

export function CreateWorkspaceModal({
  open,
  onOpenChange,
  automationServerId: propAutomationServerId,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState("");
  const [selectedAutomationServerId, setSelectedAutomationServerId] =
    useState<string>("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [remote, setRemote] = useState("");
  const [branch, setBranch] = useState("");
  const [domain, setDomain] = useState("");
  const [certsDir, setCertsDir] = useState("");
  const [verbose, setVerbose] = useState(false);
  const [mkCerts, setMkCerts] = useState(false);
  const [noIde, setNoIde] = useState(false);
  const [noDashboard, setNoDashboard] = useState(false);
  const [setHosts, setSetHosts] = useState(false);
  const [local, setLocal] = useState(false);
  const [gitopsImage, setGitopsImage] = useState("");
  const [editorImage, setEditorImage] = useState("");
  const [dashboardImage, setDashboardImage] = useState("");
  const [gitopsDevSourceDir, setGitopsDevSourceDir] = useState("");
  const [editorDevSourceDir, setEditorDevSourceDir] = useState("");
  const [dashboardDevSourceDir, setDashboardDevSourceDir] = useState("");
  const [oauthConfig, setOauthConfig] = useState("");
  const [noOauth, setNoOauth] = useState(false);
  const [sshPort, setSshPort] = useState("");
  const [staging, setStaging] = useState(false);
  const [disableWorkspaceBackups, setDisableWorkspaceBackups] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);

  // Use prop automationServerId if provided, otherwise use selected one
  const automationServerId =
    propAutomationServerId || selectedAutomationServerId;
  const showServerSelector = !propAutomationServerId;

  // Fetch automation servers if we need to show the selector
  const { data: automationServersData } = useAutomationServersQuery();
  const automationServers = useMemo(
    () => automationServersData?.pages.flatMap((page) => page.results) ?? [],
    [automationServersData]
  );

  const selectedServer = useMemo(
    () => automationServers.find((s) => s.automation_server_id === automationServerId),
    [automationServers, automationServerId]
  );

  // Set first automation server as default when modal opens and no server is selected
  useEffect(() => {
    if (
      open &&
      showServerSelector &&
      !selectedAutomationServerId &&
      automationServers.length > 0
    ) {
      setSelectedAutomationServerId(automationServers[0].automation_server_id);
    }
  }, [open, showServerSelector, selectedAutomationServerId, automationServers]);

  // Reset selected server when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedAutomationServerId("");
    }
  }, [open]);

  // Default domain from selected automation server
  useEffect(() => {
    if (selectedServer?.domain) {
      setDomain(selectedServer.domain);
    }
  }, [selectedServer]);

  // Log when modal opens/closes
  useEffect(() => {
    console.log(
      `CreateWorkspaceModal: Modal open state changed - open: ${open}, automationServerId: ${automationServerId}`
    );
  }, [open, automationServerId]);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toastShownRef = useRef(false);
  const isSubmittingRef = useRef(false); // Track submission status synchronously to prevent double submission

  // State for workspace creation using global MQTTService
  const [logs, setLogs] = useState<WorkspaceCommandLogMessage[]>([]);
  const [result, setResult] = useState<WorkspaceCommandResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const clearLogs = useCallback(() => {
    setLogs([]);
    setResult(null);
  }, []);

  // Query workspaces to find the newly created one
  const { refetch: refetchWorkspaces } = useWorkspacesQuery();

  // Get MQTT tokens refetch function to establish connection to new workspace
  const { refetch: refetchMQTTTokens } = useMQTTTokens();

  const handleSubmit = async () => {
    // Guard against double submission - check ref synchronously before any async operations
    if (isSubmittingRef.current || isLoading) {
      console.log(
        "CreateWorkspaceModal: Submission already in progress, ignoring duplicate call"
      );
      return;
    }

    if (!name.trim()) return;

    // If showing server selector, ensure one is selected
    if (showServerSelector && !selectedAutomationServerId) {
      console.error("Cannot create workspace: No automation server selected");
      return;
    }

    if (!automationServerId) {
      console.error("Cannot create workspace: No automation server ID");
      return;
    }

    console.log("handleSubmit called", {
      name: name.trim(),
      automationServerId,
    });

    // Set submission flag immediately (synchronously) to prevent race conditions
    isSubmittingRef.current = true;
    clearLogs();
    setIsLoading(true);

    const request: any = {
      name: name.trim(),
    };

    if (remote) request.remote = remote.trim();
    if (branch) request.branch = branch.trim();
    if (domain) request.domain = domain.trim();
    if (certsDir) request["certs-dir"] = certsDir.trim();
    if (verbose) request.verbose = true;
    if (mkCerts) request.mkcerts = true;
    if (noIde) request["no-ide"] = true;
    if (noDashboard) request["no-dashboard"] = true;
    if (setHosts) request["set-hosts"] = true;
    if (local) request.local = true;
    if (gitopsImage) request["gitops-image"] = gitopsImage.trim();
    if (editorImage) request["editor-image"] = editorImage.trim();
    if (dashboardImage) request["dashboard-image"] = dashboardImage.trim();
    if (gitopsDevSourceDir)
      request["gitops-dev-source-dir"] = gitopsDevSourceDir.trim();
    if (editorDevSourceDir)
      request["editor-dev-source-dir"] = editorDevSourceDir.trim();
    if (dashboardDevSourceDir)
      request["dashboard-dev-source-dir"] = dashboardDevSourceDir.trim();
    if (oauthConfig) request["oauth-config"] = oauthConfig.trim();
    if (noOauth) request["no-oauth"] = true;
    if (sshPort) request["ssh-port"] = sshPort.trim();
    if (staging) request.staging = true;
    if (!disableWorkspaceBackups) request.enableWorkspaceBackups = true;

    console.log(
      "📝 CreateWorkspaceModal: Submitting workspace creation request",
      {
        request,
        automationServerId,
        timestamp: new Date().toISOString(),
      }
    );

    try {
      // Use the global MQTTService for workspace creation
      const mqttService = MQTTService.getInstance();
      const createResult = await mqttService.createWorkspace(
        automationServerId,
        request,
        (log) => {
          // Handle log messages
          setLogs((prev) => [...prev, log]);
        }
      );

      console.log(
        "✅ CreateWorkspaceModal: Workspace creation completed:",
        createResult
      );
      setResult(createResult);
      setIsLoading(false);
      isSubmittingRef.current = false; // Reset flag on success
    } catch (error: any) {
      console.error(
        "❌ CreateWorkspaceModal: Error creating workspace:",
        error
      );
      setResult({
        "request-id": "",
        success: false,
        error: error?.message || "Failed to create workspace",
      });
      setIsLoading(false);
      isSubmittingRef.current = false; // Reset flag on error
    }
  };

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onOpenChange(false);
      // Reset form after a delay to allow dialog to close
      setTimeout(() => {
        setName("");
        setSelectedAutomationServerId("");
        setRemote("");
        setBranch("");
        setDomain("");
        setCertsDir("");
        setVerbose(false);
        setMkCerts(false);
        setNoIde(false);
        setNoDashboard(false);
        setSetHosts(false);
        setLocal(false);
        setGitopsImage("");
        setEditorImage("");
        setDashboardImage("");
        setGitopsDevSourceDir("");
        setEditorDevSourceDir("");
        setDashboardDevSourceDir("");
        setOauthConfig("");
        setNoOauth(false);
        setSshPort("");
        setStaging(false);
        setDisableWorkspaceBackups(false);
        clearLogs();
        isSubmittingRef.current = false; // Reset submission flag when modal closes
      }, 300);
    }
  }, [isLoading, onOpenChange, clearLogs]);

  // Handle success: find workspace and navigate to it
  useEffect(() => {
    if (result?.success && !isLoading && !toastShownRef.current) {
      toastShownRef.current = true;

      // Refetch workspaces to get the newly created one
      const findAndNavigate = async () => {
        // Wait a bit for workspace sync to complete
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Refetch MQTT tokens to get token for new workspace and establish connection
        await refetchMQTTTokens();

        // Refetch workspaces
        const refetchedResult = await refetchWorkspaces();
        const allWorkspaces = refetchedResult.data ?? [];

        // Wait for AutomationsProvider to re-initialize MQTTService with updated tokens/workspaces
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Find workspace by name and automation server
        const workspaceName = name.trim();
        const newWorkspace = allWorkspaces.find(
          (ws) =>
            ws.name === workspaceName &&
            ws.automation_server === automationServerId
        );

        if (newWorkspace) {
          toast.success(`Workspace "${workspaceName}" created successfully`);
          // Navigate using React Router (client-side navigation, no page reload)
          navigate(`/workspaces/${newWorkspace.id}`, { replace: false });
          // Then close modal
          handleClose();
        } else {
          // Workspace not found yet, try again after a delay
          setTimeout(async () => {
            // Refetch MQTT tokens again in case they weren't ready
            await refetchMQTTTokens();
            const retryResult = await refetchWorkspaces();
            const retryWorkspaces = retryResult.data ?? [];
            const foundWorkspace = retryWorkspaces.find(
              (ws) =>
                ws.name === workspaceName &&
                ws.automation_server === automationServerId
            );

            if (foundWorkspace) {
              // Wait for AutomationsProvider to re-initialize MQTTService with updated tokens/workspaces
              await new Promise((resolve) => setTimeout(resolve, 1000));

              toast.success(
                `Workspace "${workspaceName}" created successfully`
              );
              // Navigate using React Router (client-side navigation, no page reload)
              navigate(`/workspaces/${foundWorkspace.id}`, { replace: false });
              // Then close modal
              handleClose();
            } else {
              // Still not found, show toast and close modal anyway
              toast.success(
                `Workspace "${workspaceName}" created successfully`
              );
              handleClose();
            }
          }, 2000);
        }
      };

      findAndNavigate();
    }

    // Reset toast flag when modal closes
    if (!open) {
      toastShownRef.current = false;
    }
  }, [
    result?.success,
    isLoading,
    name,
    automationServerId,
    navigate,
    refetchWorkspaces,
    refetchMQTTTokens,
    open,
    handleClose,
    queryClient,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Workspace</DialogTitle>
          <DialogDescription>
            {showServerSelector
              ? "Create a new workspace on an automation server"
              : "Create a new workspace on this automation server"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto flex flex-col gap-4 min-h-0 pr-1">
          {showServerSelector && (
            <div className="space-y-2 flex-shrink-0">
              <Label htmlFor="automation-server">Automation Server *</Label>
              <Select
                value={selectedAutomationServerId}
                onValueChange={setSelectedAutomationServerId}
                disabled={isLoading}
              >
                <SelectTrigger id="automation-server">
                  <SelectValue placeholder="Select an automation server" />
                </SelectTrigger>
                <SelectContent>
                  {automationServers.map((server) => (
                    <SelectItem
                      key={server.automation_server_id}
                      value={server.automation_server_id}
                    >
                      {server.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2 flex-shrink-0">
            <Label htmlFor="name">Workspace Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                // Only allow lowercase letters, numbers, and hyphens
                const filteredValue = e.target.value.replace(/[^a-z0-9-]/g, "");
                setName(filteredValue);
              }}
              placeholder="my-workspace"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Only lowercase letters, numbers, and hyphens are allowed
            </p>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            <Checkbox
              id="disableWorkspaceBackups"
              checked={disableWorkspaceBackups}
              onCheckedChange={(checked) =>
                setDisableWorkspaceBackups(checked === true)
              }
              disabled={isLoading}
            />
            <Label htmlFor="disableWorkspaceBackups">
              Disable automatic workspace backups
            </Label>
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                onClick={() => setAdvancedOpen(!advancedOpen)}
                className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
              >
                Advanced Options
                {advancedOpen ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-4 pt-4">
                  {/* Quick Options - Commonly Used Flags */}
                  <div className="space-y-2 border-b pb-4">
                    <Label className="text-sm font-semibold">
                      Quick Options
                    </Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="local"
                          checked={local}
                          onCheckedChange={(checked) =>
                            setLocal(checked === true)
                          }
                          disabled={isLoading}
                        />
                        <Label htmlFor="local" className="font-medium">
                          Local (auto set-hosts and mkcerts, defaults to
                          bs-&lt;workspace-name&gt;.localhost)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="verbose"
                          checked={verbose}
                          onCheckedChange={(checked) =>
                            setVerbose(checked === true)
                          }
                          disabled={isLoading}
                        />
                        <Label htmlFor="verbose">Verbose Output</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="no-ide"
                          checked={noIde}
                          onCheckedChange={(checked) =>
                            setNoIde(checked === true)
                          }
                          disabled={isLoading}
                        />
                        <Label htmlFor="no-ide">
                          No IDE (do not start Bitswan Editor)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="no-dashboard"
                          checked={noDashboard}
                          onCheckedChange={(checked) =>
                            setNoDashboard(checked === true)
                          }
                          disabled={isLoading}
                        />
                        <Label htmlFor="no-dashboard">
                          No Dashboard (do not start the workspace-dashboard
                          sidecar)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="no-oauth"
                          checked={noOauth}
                          onCheckedChange={(checked) =>
                            setNoOauth(checked === true)
                          }
                          disabled={isLoading}
                        />
                        <Label htmlFor="no-oauth">
                          No OAuth (disable OAuth configuration)
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* Repository Options */}
                  <div className="space-y-4 border-b pb-4">
                    <Label className="text-sm font-semibold">
                      Repository Options
                    </Label>
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <Label htmlFor="remote">Remote Repository</Label>
                        <Input
                          id="remote"
                          value={remote}
                          onChange={(e) => setRemote(e.target.value)}
                          placeholder="https://github.com/user/repo.git"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="branch">Branch</Label>
                        <Input
                          id="branch"
                          value={branch}
                          onChange={(e) => setBranch(e.target.value)}
                          placeholder="main (defaults to repository's default branch)"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ssh-port">SSH Port</Label>
                        <Input
                          id="ssh-port"
                          value={sshPort}
                          onChange={(e) => setSshPort(e.target.value)}
                          placeholder="22 (for repositories behind firewalls)"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Domain & Certificate Options */}
                  <div className="space-y-4 border-b pb-4">
                    <Label className="text-sm font-semibold">
                      Domain & Certificate Options
                    </Label>
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <Label htmlFor="domain">Domain</Label>
                        <Input
                          id="domain"
                          value={domain}
                          onChange={(e) => setDomain(e.target.value)}
                          placeholder="example.com"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="certs-dir">
                          Certificates Directory
                        </Label>
                        <Input
                          id="certs-dir"
                          value={certsDir}
                          onChange={(e) => setCertsDir(e.target.value)}
                          placeholder="/path/to/certs"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="mkcerts"
                            checked={mkCerts}
                            onCheckedChange={(checked) =>
                              setMkCerts(checked === true)
                            }
                            disabled={isLoading}
                          />
                          <Label htmlFor="mkcerts">
                            Generate Certificates (mkcerts)
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="set-hosts"
                            checked={setHosts}
                            onCheckedChange={(checked) =>
                              setSetHosts(checked === true)
                            }
                            disabled={isLoading}
                          />
                          <Label htmlFor="set-hosts">
                            Set Hosts (automatically set hosts to /etc/hosts)
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Container Image Options */}
                  <div className="space-y-4 border-b pb-4">
                    <Label className="text-sm font-semibold">
                      Container Image Options
                    </Label>
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <Label htmlFor="gitops-image">GitOps Image</Label>
                        <Input
                          id="gitops-image"
                          value={gitopsImage}
                          onChange={(e) => setGitopsImage(e.target.value)}
                          placeholder="custom-gitops-image:tag"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="editor-image">Editor Image</Label>
                        <Input
                          id="editor-image"
                          value={editorImage}
                          onChange={(e) => setEditorImage(e.target.value)}
                          placeholder="custom-editor-image:tag"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dashboard-image">Dashboard Image</Label>
                        <Input
                          id="dashboard-image"
                          value={dashboardImage}
                          onChange={(e) => setDashboardImage(e.target.value)}
                          placeholder="custom-workspace-dashboard:tag"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="gitops-dev-source-dir">
                          GitOps Dev Source Directory
                        </Label>
                        <Input
                          id="gitops-dev-source-dir"
                          value={gitopsDevSourceDir}
                          onChange={(e) =>
                            setGitopsDevSourceDir(e.target.value)
                          }
                          placeholder="/path/to/source (mounts as /src/app in gitops container)"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="editor-dev-source-dir">
                          Editor Dev Source Directory
                        </Label>
                        <Input
                          id="editor-dev-source-dir"
                          value={editorDevSourceDir}
                          onChange={(e) =>
                            setEditorDevSourceDir(e.target.value)
                          }
                          placeholder="/path/to/source (enables dev-mode for the editor only)"
                          disabled={isLoading}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dashboard-dev-source-dir">
                          Dashboard Dev Source Directory
                        </Label>
                        <Input
                          id="dashboard-dev-source-dir"
                          value={dashboardDevSourceDir}
                          onChange={(e) =>
                            setDashboardDevSourceDir(e.target.value)
                          }
                          placeholder="/path/to/bitswan-workspace-dashboard (enables dev-mode for the dashboard only)"
                          disabled={isLoading}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Dev-mode is enabled per service: setting only one
                        source directory puts only that container into
                        hot-reload mode; the other runs the pre-built image.
                      </p>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="staging"
                          checked={staging}
                          onCheckedChange={(checked) =>
                            setStaging(checked === true)
                          }
                          disabled={isLoading}
                        />
                        <Label htmlFor="staging">
                          Use staging images
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* OAuth Options */}
                  <div className="space-y-4">
                    <Label className="text-sm font-semibold">
                      OAuth Options
                    </Label>
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <Label htmlFor="oauth-config">OAuth Config File</Label>
                        <Input
                          id="oauth-config"
                          value={oauthConfig}
                          onChange={(e) => setOauthConfig(e.target.value)}
                          placeholder="/path/to/oauth-config.json"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-16 w-16 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Creating workspace...
              </p>
              {(logs.length > 0 || isLoading) && (
                <button
                  type="button"
                  onClick={() => setShowLogsDialog(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  show logs...
                </button>
              )}
            </div>
          )}

          {!isLoading && logs.length > 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setShowLogsDialog(true)}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                show logs...
              </button>
            </div>
          )}

          {result && (
            <div
              className={`p-3 rounded-md ${
                result.success
                  ? "bg-green-50 text-green-900 border border-green-200"
                  : "bg-red-50 text-red-900 border border-red-200"
              }`}
            >
              {result.success ? (
                <div>
                  ✓ {result.message || "Workspace created successfully"}
                </div>
              ) : (
                <div>✗ {result.error || "Workspace creation failed"}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {result?.success ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !name.trim() ||
              isLoading ||
              !automationServerId ||
              (showServerSelector && !selectedAutomationServerId)
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Workspace"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <LogsDialog
        open={showLogsDialog}
        onOpenChange={setShowLogsDialog}
        logs={logs as LogEntry[]}
        isLoading={isLoading}
        title="Workspace Creation Logs"
      />
    </Dialog>
  );
}
